<script setup>
import CollectionsPreviewBasicBlock from "~/components/organisms/CollectionsPreviewBasicBlock.vue";
import CollectionsFilter from "~/components/organisms/CollectionsFilter.vue";
import Text from "~/components/atoms/Text.vue";
import Heading from "~/components/atoms/Heading.vue";
import Pagination from "~/components/molecules/Pagination.vue";
import Timer from "~/components/molecules/Timer.vue";
import Button from "~/components/atoms/Button.vue";
import FilterIcon from "~/components/icons/FilterIcon.vue";
import CrossIcon from "~/components/icons/CrossIcon.vue";
import {useRoute, useRouter} from 'vue-router';
import {fetchWithErrorHandling} from '~/composables/fetchWithErrorHandling';

const {t} = useI18n();
const route = useRoute();
const router = useRouter();

const parseFiltersFromQuery = (query) => {
  const filters = {}

  for (const key of ['niche', 'group']) {
    const raw = query[key]
    if (typeof raw === 'string' && raw.length) {
      filters[key] = [...new Set(raw.split('_').filter(Boolean))]
    }
  }

  return filters
}

const page = ref(parseInt(route.query.page, 10) || 1)
const chosenFilters = ref(parseFiltersFromQuery(route.query))
const openFilter = ref(0)
const filterComponent = ref(null)
const loading = ref(false)

const buildQuery = (filters, pageNum) => {
  const query = {}

  for (const [key, values] of Object.entries(filters || {})) {
    if (Array.isArray(values) && values.length) {
      query[key] = values.join('_')
    }
  }

  if (pageNum > 1) {
    query.page = pageNum
  }

  return query
}

const data = await fetchWithErrorHandling('collection', {
  lazy: false,
  query: buildQuery(chosenFilters.value, page.value),
})

useHead({
  title: t('collection.alternative_collection'),
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: t('collection.meta_description')
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: t('meta.domain') + '/img/og-default.jpg'
    }
  ],
  link: [
    {key: 'image_src', rel: 'image_src', href: t('meta.domain') + '/img/og-default.jpg'}
  ],
  script: [
    {
      type: 'application/ld+json',
      children: data.value.breadcrumbs_structured_data
    },
  ]
})

const total = computed(() => data.value ? data.value.total : 0);
const per_page = computed(() => data.value ? data.value.per_page : 0);
const pages_count = computed(() => data.value ? data.value.pages_count : 1);
const collections = computed(() => data.value ? data.value.collections : []);
const counter = computed(() => data.value?.counter);
const filter = computed(() => data.value?.filter ?? []);
const filterTitles = computed(() =>
  Object.fromEntries((filter.value || []).map(item => [item.slug, item.title]))
)

const labelForFilterValue = (filterSlug, valueSlug) => {
  const filterItem = (filter.value || []).find(item => item.slug === filterSlug)
  const match = filterItem?.values?.find(item => item.slug === valueSlug)
  return match?.value ?? valueSlug
}

const syncUrl = (filters, pageNum) => {
  if (!import.meta.client) {
    return
  }

  router.replace({
    path: route.path,
    query: buildQuery(filters, pageNum),
  })
}

const fetchCollections = async (newPage, filters = chosenFilters.value) => {
  loading.value = true

  const newData = await fetchWithErrorHandling('collection', {
    lazy: false,
    query: buildQuery(filters, newPage),
  });

  if (newData.value) {
    data.value = newData.value;
    chosenFilters.value = newData.value.chosen_filters || filters || {}
    window.scrollTo(0, 0);
    syncUrl(chosenFilters.value, newPage);
  }

  loading.value = false
};

const handlePageChange = (newPage) => {
  if (newPage === page.value) {
    return;
  }

  page.value = newPage;
  fetchCollections(newPage);
};

const handleFilterChange = (filters) => {
  chosenFilters.value = filters
  page.value = 1
  fetchCollections(1, filters)
}

const removeFilter = (e) => {
  filterComponent.value?.handleClick(e)
}

const clearFilter = () => {
  filterComponent.value?.clearFilter()
}
</script>

<template>
  <div class="big-page-wrapper">
    <div class="page-wrapper">
      <div class="container g-32">
        <div class="g-16">
          <div class="g-8">
            <Heading>{{ $t('collection.most_popular') }}</Heading>
            <Text>{{ $t('collection.list_sub_title') }}</Text>
          </div>

          <div class="category-timer-wrapper" v-if="counter?.date">
            <Timer
              :background="counter.pc_background"
              :text="counter.pc_text"
              :color="counter.pc_color"
              :timer_color="counter.color"
              :date="counter.date"
            />
          </div>

          <div class="row full-width header">
            <div class="row g-32 btn-block">
              <Button @click="openFilter = Math.random() * 100" class="reset middle uppercase" type="black">
                <FilterIcon width="20px" height="18px"/>
                &nbsp;
                {{ $t('base.filter_action') }}
              </Button>
              <Text v-if="$device.isDesktop" tag="b" class="counter lowercase">
                {{ total }} {{ $t('collection.alternative_collection').toLowerCase() }}
              </Text>
            </div>
          </div>

          <div class="filter-labels row g-32 wrap" v-if="Object.keys(chosenFilters || {}).length">
            <div class="row g-24 wrap">
              <template v-for="([filterKey, values]) in Object.entries(chosenFilters)" :key="filterKey">
                <Button
                  v-for="item in values"
                  :key="`${filterKey}-${item}`"
                  @click="removeFilter({name: filterKey, value: item})"
                  class="filter-label uppercase wrap"
                  type="mini-label"
                >
                  <div class="row g-4">
                    <span v-if="filterTitles[filterKey]" class="filter-label-name">{{ filterTitles[filterKey] }}:</span>
                    {{ labelForFilterValue(filterKey, item) }}
                    <CrossIcon/>
                  </div>
                </Button>
              </template>
            </div>
            <Text class="clickable clear" @click="clearFilter" underline>{{ $t("base.reset_filters") }}</Text>
          </div>

          <CollectionsFilter
            ref="filterComponent"
            :openFilter="openFilter"
            :filter="filter"
            :chosenFilters="chosenFilters"
            @change="handleFilterChange"
          />

          <CollectionsPreviewBasicBlock :no-carousel="true" :key="`${page}-${JSON.stringify(chosenFilters)}`" :collections="collections"
                                        :menu_items="[]"/>
        </div>

        <Pagination @change="handlePageChange" entity="collection" :current-page="page"
                    :count="collections.length"
                    :items-per-page="per_page" :total-items="total"
                    :total-pages="pages_count"/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.category-timer-wrapper {
  @include smd {
    margin: 0 -16px;
  }

  :deep(.timer) {
    padding: 0 10px;
  }
}

.header {
  justify-content: space-between;
  align-items: center;
}

.btn-block {
  align-items: center;

  // Button type="black" has a fixed 101px width, too narrow for localized labels.
  button {
    width: auto;
    min-width: 165px;
    padding: 0 20px;

    @include smd {
      min-width: 140px;
    }
  }
}

.counter {
  white-space: nowrap;
}

.filter-labels {
  align-items: center;

  .filter-label {
    :deep(svg) {
      width: 10px;
      height: 10px;
    }
  }

  .filter-label-name {
    opacity: 0.7;
  }

  .clear {
    white-space: nowrap;
  }
}
</style>
